import { Skeleton } from '@strivelabs/ui-core/v2';
import { cn } from '@strivelabs/ui-core/lib/utils';
import { useTaskArtifacts } from '@/hooks/useArtifacts';
import { ANALYTICS_EVENTS } from '@/lib/analytics/events';
import { useTrack } from '@/lib/analytics/useTrack';
import type { AttachedArtifact } from '@/types/artifact';
import { EMPTY_VALUE } from '../taskTheme';
import { TaskAttachmentCard } from './TaskAttachmentCard';
import { TaskSection } from './TaskSection';

interface TaskAttachmentsProps {
  taskId: string;
  /**
   * How to open one. Required, because every host of this panel has somewhere
   * to show a page: a host with nowhere to put it would leave the cards inert.
   */
  onOpenArtifact: (artifact: AttachedArtifact) => void;
}

/**
 * The artifacts a task holds: its notes, and its outputs.
 *
 * Everything here is attached and detached through chat, so the panel only
 * reads. A section with nothing in it is left out rather than shown empty:
 * most tasks hold nothing, and an empty heading on every one of them is noise.
 */
export const TaskAttachments = ({ taskId, onOpenArtifact }: TaskAttachmentsProps) => {
  const { data, isLoading, isError } = useTaskArtifacts(taskId);
  const trackEvent = useTrack();

  const open = (artifact: AttachedArtifact) => {
    trackEvent(ANALYTICS_EVENTS.ARTIFACT_OPENED, { via: 'task_detail' });
    onOpenArtifact(artifact);
  };

  if (isLoading) {
    return (
      <TaskSection title="Attachments">
        <Skeleton className="h-[132px] w-full rounded-[10px]" />
      </TaskSection>
    );
  }

  if (isError) {
    return (
      <TaskSection title="Attachments">
        <p className={cn(EMPTY_VALUE, 'text-[13px]')}>Couldn&apos;t load attachments.</p>
      </TaskSection>
    );
  }

  return (
    <>
      <AttachmentGroup title="Notes" artifacts={data?.notes ?? []} onOpen={open} />
      <AttachmentGroup title="Outputs" artifacts={data?.outputs ?? []} onOpen={open} />
    </>
  );
};

interface AttachmentGroupProps {
  title: string;
  artifacts: AttachedArtifact[];
  onOpen: (artifact: AttachedArtifact) => void;
}

/** One slot's grid, or nothing when the slot is empty. */
const AttachmentGroup = ({ title, artifacts, onOpen }: AttachmentGroupProps) => {
  if (artifacts.length === 0) {
    return null;
  }

  return (
    <TaskSection title={title} count={artifacts.length}>
      <div className="grid grid-cols-1 gap-2.5 min-[560px]:grid-cols-2">
        {artifacts.map((artifact) => (
          <TaskAttachmentCard
            key={`${artifact.relation_type}-${artifact.id}`}
            artifact={artifact}
            onOpen={() => onOpen(artifact)}
          />
        ))}
      </div>
    </TaskSection>
  );
};
